'use client';

import HeaderSkeleton, { type OwnProps as HeaderProps } from '@carvana/header';
import { useClientSide } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { isHomePathname } from '~/constants/common/homeAppRouter';
import { commonEnvironment, staticCdnUrl } from '~/constants/environment';
import { useShowComposerTreatment } from '~/context/composer/ComposerContext';
import useHeaderNavigationConfig from '~/hooks/common/composer/useHeaderNavigationConfig';
import { wait } from '~/utilities';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

type OwnProps = {
  cookies?: Record<string, string>;
  pathname?: string;
};

/**
 * Client Component for PageHeader
 * This is a simplfied page header that have access to context providers
 * The server component CarvanaLayoutHeader handles the context logic
 */
function ClientPageHeader({
  cookies = {},
  pathname: serverPathname
}: OwnProps) {
  const isClientSide = useClientSide();
  const pathname = usePathname() ?? serverPathname;
  const isHome = isHomePathname(pathname);
  const showComposerTreatment = useShowComposerTreatment() && !isHome;
  const showChatUi = !pathname?.startsWith('/new') && !showComposerTreatment;
  const navigationConfig = useHeaderNavigationConfig(pathname);

  useEffect(() => {
    wait(100).then(() => {
      void MfeLoad({
        files: [
          [
            'HeaderModule',
            [
              {
                component: 'default',
                default: {
                  file: 'index.js',
                  manifest: `${staticCdnUrl}/header-module/header/assets-manifest.json?v=${getMfeCacheBuster()}`
                }
              }
            ]
          ]
        ]
      });
    });
  }, []);

  const headerProps = {
    cookies,
    environment: commonEnvironment,
    navigationConfig,
    route: isHome ? '/' : pathname,
    showChatUi,
    showComposerTreatment
  } as HeaderProps;

  return (
    <div id="mfe-header-wrapper">
      {isClientSide ? (
        <MfeReactComponent
          component="default"
          fallback={{
            Component: <HeaderSkeleton {...headerProps} />,
            timeout: 30000
          }}
          loading={<HeaderSkeleton {...headerProps} />}
          module="HeaderModule"
          {...headerProps}
        />
      ) : (
        <HeaderSkeleton {...headerProps} />
      )}
    </div>
  );
}

export default withErrorBoundary<OwnProps>(ClientPageHeader, {
  section: 'PageHeader'
});
